import React from 'react';
import { getRefresh } from '../../utils/web-apis-client';
import { get, enums, parseIntOrDefault } from '../../utils';
import { PersonDataContext } from '../../contexts/person-data-context';
import { log } from '../../utils/log';
import { KenticoDataContext } from '../kentico-data-context';

// const log = new Logger\('contexts/refresh-context');

export type LoginRefreshStatuses =
  | enums.RefreshStatus.refreshed
  | enums.RefreshStatus.failed
  | enums.RefreshStatus.started
  | enums.RefreshStatus.failedWithData
  | enums.RefreshStatus.notRegisterable;

export type FullRefreshStatuses =
  | enums.RefreshStatus.refreshed
  | enums.RefreshStatus.failed
  | enums.RefreshStatus.started;

export const RefreshContext = React.createContext<{
  loginStatus: LoginRefreshStatuses;
  status: FullRefreshStatuses;
  refreshDetails: any;
  isRefreshing: boolean;
  usingCachedData: boolean;
  setRefreshDetails: any;
  pollForUpdates: any;
}>({
  loginStatus: enums.RefreshStatus.refreshed,
  status: enums.RefreshStatus.started,
  refreshDetails: {} as any,
  isRefreshing: false,
  usingCachedData: false,
  setRefreshDetails: () => {},
  pollForUpdates: () => {},
});

export const handleFullRefreshPolling = async (
  setStatus: React.Dispatch<React.SetStateAction<FullRefreshStatuses>>,
  setRefreshDetails: React.Dispatch<React.SetStateAction<any>>,
  refreshPersonData: () => Promise<void>,
  setTriggerBackgroundUpdates: React.Dispatch<React.SetStateAction<boolean>>,
  interval: NodeJS.Timeout,
  setUsingCachedData?: React.Dispatch<React.SetStateAction<boolean>>,
  onRefreshComplete?: () => void,
) => {
  try {
    const result = await getRefresh();

    const rs = get(result, 'fullRefreshStatus.status', '');
    const loginRs = get(result, 'loginRefreshStatus.status', '');
    const rd = get(result, 'statusDetail', {});

    // Full refresh is complete when its status is a non-started value,
    // OR when its status is empty but login refresh has finished
    // (backend may not populate fullRefreshStatus separately).
    const fullDone = rs.length > 0 && rs !== enums.RefreshStatus.started;
    const loginDone =
      loginRs.length > 0 && loginRs !== enums.RefreshStatus.started;
    const complete = fullDone || (rs === '' && loginDone);

    if (complete) {
      log.debug('Full refresh complete');
      setRefreshDetails(rd);
      setStatus(fullDone ? rs : enums.RefreshStatus.refreshed);
      await refreshPersonData();
      setTriggerBackgroundUpdates(false);
      setUsingCachedData?.(false);
      onRefreshComplete?.();
      clearInterval(interval);
    }
  } catch (e) {
    log.error('GET refresh failed', e);
    setStatus(enums.RefreshStatus.failed);
    setTriggerBackgroundUpdates(false);
    clearInterval(interval);
  }
};

/**
 * Backoff schedule (in ms, relative to completion) used to re-fetch personData
 * after the full refresh reports complete, to pick up eventually-consistent
 * backend writes that land shortly after.
 */
export const BACKOFF_REFRESH_DELAYS_MS = [5000, 10000];

/**
 * Schedules a series of personData refreshes on a backoff schedule
 * (5s, 10s after completion). Returns the timeout handles so
 * the caller can cancel any that are still pending.
 * @param refreshPersonData Function that re-fetches personData
 * @returns The scheduled timeout handles
 */
export const scheduleBackoffPersonDataRefreshes = (
  refreshPersonData: () => Promise<void>,
): NodeJS.Timeout[] => {
  const timeouts: NodeJS.Timeout[] = [];
  let elapsedMs = 0;
  BACKOFF_REFRESH_DELAYS_MS.forEach((delayMs) => {
    elapsedMs += delayMs;
    const timeout = setTimeout(async () => {
      try {
        await refreshPersonData();
      } catch (e) {
        log.error('Backoff personData refresh failed', e);
      }
    }, elapsedMs);
    timeouts.push(timeout);
  });
  return timeouts;
};

/**
 * Provides data from Kentico, allows for refreshes
 * @param p Props
 * @returns React Context Provider
 */
export const RefreshContextProvider = (p: {
  children: any;
  loginStatus: LoginRefreshStatuses;
  status?: FullRefreshStatuses;
  details?: any;
  triggerBackgroundUpdates?: boolean;
  usingCachedData?: boolean;
}) => {
  const [loginStatus, setLoginStatus] = React.useState<LoginRefreshStatuses>(
    p.loginStatus ?? enums.RefreshStatus.refreshed,
  );
  const [status, setStatus] = React.useState<FullRefreshStatuses>(
    p.status ?? enums.RefreshStatus.started,
  );

  const [triggerBackgroundUpdates, setTriggerBackgroundUpdates] =
    React.useState<boolean>(p.triggerBackgroundUpdates || false);

  const [refreshDetails, setRefreshDetails] = React.useState<any>(p?.details);
  const [usingCachedData, setUsingCachedData] = React.useState<boolean>(
    p.usingCachedData || false,
  );
  const { refreshPersonData } = React.useContext(PersonDataContext);
  const { config } = React.useContext(KenticoDataContext);
  const backoffTimeoutsRef = React.useRef<NodeJS.Timeout[]>([]);

  // After the full refresh reports complete, some backend writes are only
  // eventually consistent. Re-fetch personData on a backoff schedule
  // (2.5s, 5s, 10s, 20s, 40s after completion) to pick up data that lands
  // shortly after the completion signal.
  const startBackoffPersonDataRefreshes = React.useCallback(() => {
    backoffTimeoutsRef.current.forEach(clearTimeout);
    backoffTimeoutsRef.current =
      scheduleBackoffPersonDataRefreshes(refreshPersonData);
  }, [refreshPersonData]);

  // Clear any pending backoff refreshes on unmount
  React.useEffect(() => {
    return () => {
      backoffTimeoutsRef.current.forEach(clearTimeout);
      backoffTimeoutsRef.current = [];
    };
  }, []);

  const pollForUpdates = () => {
    setTriggerBackgroundUpdates(true);
  };

  React.useEffect(() => {
    p.loginStatus && setLoginStatus(p.loginStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (triggerBackgroundUpdates) {
      const refreshCheckInterval = parseIntOrDefault(
        get(config, enums.RemoteConfigKeys.fullRefreshCheckIntervalMs),
        2000,
      );
      const refreshCheckCount = parseIntOrDefault(
        get(config, enums.RemoteConfigKeys.fullRefreshCheckCount),
        60,
      );
      let attempts = 0;
      interval ??= setInterval(async () => {
        await handleFullRefreshPolling(
          setStatus,
          setRefreshDetails,
          refreshPersonData,
          setTriggerBackgroundUpdates,
          interval,
          setUsingCachedData,
          startBackoffPersonDataRefreshes,
        );

        attempts++;
        if (attempts >= refreshCheckCount) {
          log.error('Full refresh timeout');
          setStatus(enums.RefreshStatus.failed);
          setTriggerBackgroundUpdates(false);
          setUsingCachedData(false);
          clearInterval(interval);
        }
      }, refreshCheckInterval);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerBackgroundUpdates]);

  return (
    <RefreshContext.Provider
      value={{
        loginStatus,
        status,
        isRefreshing: triggerBackgroundUpdates,
        usingCachedData,
        refreshDetails,
        setRefreshDetails,
        pollForUpdates,
      }}
    >
      {p.children}
    </RefreshContext.Provider>
  );
};
